import LanguageIcon from '@mui/icons-material/Language';
import PersonIcon from '@mui/icons-material/Person';
import PsychologyIcon from '@mui/icons-material/Psychology';
import SchoolIcon from '@mui/icons-material/School';
import { AnimatePresence } from 'framer-motion';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { useAppState } from '../../../Context/AppStateContext';
import { useSanityData } from '../../../Context/SanityDataContext';
import { useSanityLocalized } from '../../../hooks/useSanityLocalized';
import { theme } from '../../../theme';
import BorderBox from '../../ui/BorderBox.component';
import Modal from '../../ui/Modal.component';
import MotionContainer from '../../ui/MotionContainer.component';
import ScrollableContent from '../../ui/ScrollableContent.component';
import TabSelector from '../../ui/TabSelector.component';

interface AboutProps {
  visible: boolean;
  onClose: () => void;
  onFocus?: () => void;
  isActive?: boolean;
}

const AboutContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  padding: 20px 0 20px 20px;
  overflow: hidden;
`;

const ProfileHeader = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 20px;
`;

const Name = styled.h2`
  margin: 0;
  color: ${theme.colors.green};
  font-family: 'Courier New', monospace;
  font-size: 24px;
`;

const Title = styled.h3`
  margin: 5px 0 0 0;
  color: ${theme.colors.textMuted};
  font-family: 'Courier New', monospace;
  font-size: 16px;
  font-weight: normal;
`;

const SectionTitle = styled.h4`
  color: ${theme.colors.green};
  margin: 20px 0 15px 0;
  display: flex;
  align-items: center;
  border-bottom: 1px solid rgba(31, 207, 94, 0.3);
  padding-bottom: 8px;
  font-family: 'Courier New', monospace;

  svg {
    margin-right: 12px;
    font-size: 20px;
  }
`;

const IntroductionSection = styled.div`
  line-height: 1.7;
  color: ${theme.colors.text};

  p {
    margin-bottom: 15px;
    font-size: 15px;

    &:last-child {
      margin-bottom: 0;
    }
  }
`;

const CompetenciesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 12px;
  margin-top: 10px;
`;

const CompetencyBadge = styled.div`
  background-color: rgba(31, 207, 94, 0.1);
  border: 1px solid ${theme.colors.green};
  color: ${theme.colors.green};
  padding: 10px 15px;
  border-radius: 4px;
  font-family: 'Courier New', monospace;
  font-size: 14px;
  display: flex;
  align-items: center;

  &::before {
    content: '>';
    margin-right: 10px;
    color: ${theme.colors.green};
  }
`;

const LanguageItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 15px;
  background-color: rgba(31, 207, 94, 0.05);
  border-left: 3px solid ${theme.colors.green};
  margin-bottom: 10px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const LanguageName = styled.span`
  color: ${theme.colors.green};
  font-family: 'Courier New', monospace;
  font-weight: bold;
`;

const LanguageLevel = styled.span`
  color: ${theme.colors.textMuted};
  font-size: 14px;
`;

const EducationCard = styled.div`
  background-color: rgba(31, 207, 94, 0.05);
  border: 1px solid rgba(31, 207, 94, 0.2);
  border-radius: 4px;
  padding: 15px;
  margin-bottom: 15px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const EducationDegree = styled.h5`
  margin: 0 0 8px 0;
  color: ${theme.colors.green};
  font-family: 'Courier New', monospace;
  font-size: 16px;
`;

const EducationInstitution = styled.p`
  margin: 0 0 5px 0;
  color: ${theme.colors.text};
  font-size: 14px;
`;

const EducationPeriod = styled.span`
  color: ${theme.colors.textMuted};
  font-size: 13px;
`;

const EducationDescription = styled.p`
  margin: 10px 0 0 0;
  color: ${theme.colors.textMuted};
  font-size: 14px;
  line-height: 1.5;
`;

const LoadingText = styled.div`
  color: ${theme.colors.green};
  font-family: 'Courier New', monospace;
  padding: 20px;
`;

const About: React.FC<AboutProps> = ({ visible, onClose, onFocus, isActive }) => {
  const { t } = useTranslation();
  const { isOnMobile } = useAppState();
  const { person, education, isLoading } = useSanityData();
  const { getLocalizedValue, getLocalizedText } = useSanityLocalized();
  const [activeTab, setActiveTab] = useState<'profile' | 'background'>('profile');

  const handleTabChange = (id: string | number) => {
    setActiveTab(id as 'profile' | 'background');
  };

  if (!visible) return null;

  // Show loading state
  if (isLoading) {
    return (
      <Modal
        color={theme.colors.green}
        header={t('modals.about.header')}
        widthString={isOnMobile ? '90%' : '800px'}
        xPositionPercent={isOnMobile ? 50 : 40}
        yPositionPercent={20}
        onClose={onClose}
        onFocus={onFocus}
        isActive={isActive}
        icon={<PersonIcon />}
      >
        <AboutContainer>
          <LoadingText>&gt; {t('modals.about.loading')}</LoadingText>
        </AboutContainer>
      </Modal>
    );
  }

  // Get data from Sanity
  const personName = person?.name || 'Arian Garshi';
  const personTitle = getLocalizedValue(person?.title, 'Senior Frontend Developer');

  // Tab items
  const tabItems = [
    {
      id: 'profile',
      label: t('modals.about.tabs.profile'),
      icon: <PersonIcon fontSize="small" />,
    },
    {
      id: 'background',
      label: t('modals.about.tabs.background'),
      icon: <SchoolIcon fontSize="small" />,
    },
  ];

  return (
    <Modal
      color={theme.colors.green}
      header={t('modals.about.header')}
      widthString={isOnMobile ? '90%' : '800px'}
      xPositionPercent={isOnMobile ? 50 : 40}
      yPositionPercent={20}
      onClose={onClose}
      onFocus={onFocus}
      isActive={isActive}
      icon={<PersonIcon />}
    >
      <AboutContainer>
        <TabSelector items={tabItems} activeId={activeTab} onTabChange={handleTabChange} />

        <ScrollableContent>
          <AnimatePresence mode="wait">
            {activeTab === 'profile' ? (
              <MotionContainer animationKey="profile">
                <BorderBox isActive={true} noBackground={true}>
                  <ProfileHeader>
                    <Name>{personName}</Name>
                    <Title>{personTitle}</Title>
                  </ProfileHeader>

                  {/* Introduction - All 8 paragraphs from Sanity */}
                  {person?.introduction && person.introduction.length > 0 && (
                    <>
                      <SectionTitle>
                        <PersonIcon /> {t('modals.about.sections.introduction')}
                      </SectionTitle>
                      <IntroductionSection>
                        {person.introduction.map((intro, idx) => (
                          <p key={intro._key || idx}>{getLocalizedText(intro)}</p>
                        ))}
                      </IntroductionSection>
                    </>
                  )}

                  {/* Core Competencies */}
                  {person?.coreCompetencies && person.coreCompetencies.length > 0 && (
                    <>
                      <SectionTitle>
                        <PsychologyIcon /> {t('modals.about.sections.coreCompetencies')}
                      </SectionTitle>
                      <CompetenciesGrid>
                        {person.coreCompetencies.map((competency, idx) => (
                          <CompetencyBadge key={idx}>
                            {getLocalizedValue(competency)}
                          </CompetencyBadge>
                        ))}
                      </CompetenciesGrid>
                    </>
                  )}
                </BorderBox>
              </MotionContainer>
            ) : (
              <MotionContainer animationKey="background">
                <BorderBox isActive={true} noBackground={true}>
                  {/* Languages */}
                  {person?.languages && person.languages.length > 0 && (
                    <>
                      <SectionTitle>
                        <LanguageIcon /> {t('modals.about.sections.languages')}
                      </SectionTitle>
                      {person.languages.map((lang, idx) => (
                        <LanguageItem key={lang._key || idx}>
                          <LanguageName>{getLocalizedValue(lang.language)}</LanguageName>
                          <LanguageLevel>{getLocalizedValue(lang.level)}</LanguageLevel>
                        </LanguageItem>
                      ))}
                    </>
                  )}

                  {/* Education */}
                  {education && education.length > 0 && (
                    <>
                      <SectionTitle>
                        <SchoolIcon /> {t('modals.about.sections.education')}
                      </SectionTitle>
                      {education
                        .sort((a, b) => (b.order || 0) - (a.order || 0))
                        .map((edu, idx) => (
                          <EducationCard key={edu._id || idx}>
                            <EducationDegree>{getLocalizedValue(edu.degree)}</EducationDegree>
                            <EducationInstitution>{edu.institution}</EducationInstitution>
                            <EducationPeriod>{edu.period}</EducationPeriod>
                            {edu.description && (
                              <EducationDescription>
                                {getLocalizedText(edu.description)}
                              </EducationDescription>
                            )}
                          </EducationCard>
                        ))}
                    </>
                  )}
                </BorderBox>
              </MotionContainer>
            )}
          </AnimatePresence>
        </ScrollableContent>
      </AboutContainer>
    </Modal>
  );
};

export default About;
